import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ApiRequestError,
  canViewOrderTeamPrivacy,
  fetchSystemUserInfo,
  showApiResultModal,
  type SystemUserInfo,
} from '@/api';
import {
  canTeamAdminEditOrderBeforeComplete,
  canTeamAdminEditWorkHours,
  fetchOrderDetail,
  orderHasAssignedStaff,
  isAssignedServiceFullyCompleted,
  OrderCloseAfterPaymentError,
  OrderClosedBeforePaymentError,
  patchOrderAfterAssignment,
  patchOrderAfterOperation,
  patchOrderAfterServiceCompleted,
  patchOrderAfterStatusChange,
  postConfirmInvoicePaymentAndClose,
  postMarkServiceCompleted,
  postAdminReorder,
  postAssignOrder,
  postOrderBillingAdjustment,
  resolveInvoiceCloseAction,
} from '@/api/modules/order';
import type { Order, OrderServicerInfo } from '@/types/order';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import OrderDetailView from '@/pages/orders/components/OrderDetailView';
import InvoiceOrderModal from '@/pages/orders/components/InvoiceOrderModal';
import ConfirmPaymentCloseModal, {
  invoiceCloseActionLabel,
} from '@/pages/orders/components/ConfirmPaymentCloseModal';
import { rememberInvoiceSent } from '@/lib/invoiceSentOrders';
import ReorderOrderModal, { type ReorderOrderConfirmPayload } from '@/pages/orders/components/ReorderOrderModal';
import AssignOrderModal from '@/pages/orders/components/AssignOrderModal';
import type { OrderBillingValues } from '@/lib/orderBilling';

function readNavFrom(state: unknown): string | undefined {
  if (!state || typeof state !== 'object') return undefined;
  const from = (state as { from?: unknown }).from;
  return typeof from === 'string' && from.trim() ? from.trim() : undefined;
}

let pendingOrderDetailFetch: {
  orderId: string;
  promise: ReturnType<typeof fetchOrderDetail>;
} | null = null;

function fetchOrderDetailDeduped(orderId: string) {
  if (pendingOrderDetailFetch?.orderId === orderId) {
    return pendingOrderDetailFetch.promise;
  }
  const promise = fetchOrderDetail(orderId).finally(() => {
    if (pendingOrderDetailFetch?.orderId === orderId) {
      pendingOrderDetailFetch = null;
    }
  });
  pendingOrderDetailFetch = { orderId, promise };
  return promise;
}

export default function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = readNavFrom(location.state) ?? '/orders';

  const [profile, setProfile] = useState<SystemUserInfo | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completeSubmitting, setCompleteSubmitting] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState<Order | null>(null);
  const [paymentOrder, setPaymentOrder] = useState<Order | null>(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [reorderSubmitting, setReorderSubmitting] = useState(false);
  const [assignOrder, setAssignOrder] = useState<Order | null>(null);
  const [assignMode, setAssignMode] = useState<'assign' | 'billing'>('billing');
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSystemUserInfo()
      .then((info) => {
        if (!cancelled) setProfile(info);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      setError('Order not found.');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    fetchOrderDetailDeduped(orderId)
      .then((detail) => {
        if (!cancelled) setOrder(detail);
      })
      .catch((e) => {
        if (!cancelled) {
          setOrder(null);
          setError(e instanceof ApiRequestError ? e.message : 'Failed to load order detail.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const handleCompleteService = useCallback(async (target: Order) => {
    if (completeSubmitting) return;
    setCompleteSubmitting(true);
    try {
      const result = await postMarkServiceCompleted({ orderId: target.id });
      const allCompleted = isAssignedServiceFullyCompleted(target, result.allCompleted);
      setOrder((prev) => (
        prev && prev.id === target.id
          ? patchOrderAfterServiceCompleted(prev, allCompleted)
          : prev
      ));
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to mark service completed.',
        success: false,
      });
    } finally {
      setCompleteSubmitting(false);
    }
  }, [completeSubmitting]);

  const markInvoiceSent = useCallback((sent: Order) => {
    rememberInvoiceSent(sent.id);
    setOrder((prev) => (prev && prev.id === sent.id ? { ...prev, invoiceSent: true } : prev));
  }, []);

  const handleConfirmPaymentClose = useCallback(async (
    target: Order,
    details?: { paymentMethod?: 'transfer' | 'cash'; paymentDate?: string },
  ) => {
    if (paymentSubmitting) return;
    const action = resolveInvoiceCloseAction(target);
    if (!action) return;
    setPaymentSubmitting(true);
    try {
      const result = await postConfirmInvoicePaymentAndClose(target, action, details);
      setOrder((prev) => (
        prev && prev.id === target.id
          ? patchOrderAfterStatusChange(
            { ...prev, payStatus: result.payStatus, invoiceSent: true },
            result.closed ? 'completed' : prev.status,
            result.closed ? 3 : (prev.rawStatus ?? 2),
          )
          : prev
      ));
      setPaymentOrder(null);
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      if (e instanceof OrderClosedBeforePaymentError) {
        setOrder((prev) => (
          prev && prev.id === target.id
            ? patchOrderAfterStatusChange(
              { ...prev, payStatus: 0, invoiceSent: true },
              'completed',
              3,
            )
            : prev
        ));
        setPaymentOrder(null);
      } else if (e instanceof OrderCloseAfterPaymentError) {
        setOrder((prev) => (
          prev && prev.id === target.id ? { ...prev, payStatus: 1, invoiceSent: true } : prev
        ));
        setPaymentOrder((current) => (current && current.id === target.id ? { ...current, payStatus: 1, invoiceSent: true } : current));
      }
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to confirm payment.',
        success: false,
      });
    } finally {
      setPaymentSubmitting(false);
    }
  }, [paymentSubmitting]);

  const handleConfirmReorder = useCallback(async (payload: ReorderOrderConfirmPayload) => {
    if (!order || reorderSubmitting) return;
    setReorderSubmitting(true);
    try {
      const result = await postAdminReorder({
        orderId: order.id,
        date: payload.date,
        time: payload.time,
        otherInfo: payload.otherInfo,
      });
      setReorderOpen(false);
      showApiResultModal({ message: result.message, success: true });
      navigate('/team', { state: { focusDate: payload.date } });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to reorder.',
        success: false,
      });
    } finally {
      setReorderSubmitting(false);
    }
  }, [navigate, order, reorderSubmitting]);

  const handleConfirmAssign = useCallback(async (
    operatorIds: string[] | undefined,
    billing: OrderBillingValues,
    staff: OrderServicerInfo[],
  ) => {
    if (!assignOrder || assignSubmitting) return;
    if (assignMode === 'assign' && (!operatorIds || operatorIds.length === 0)) return;
    setAssignSubmitting(true);
    try {
      const result = assignMode === 'assign'
        ? await postAssignOrder({
            orderId: assignOrder.id,
            operatorIds: operatorIds ?? [],
            serviceDuration: billing.serviceDuration,
          })
        : await postOrderBillingAdjustment(assignOrder, billing, operatorIds);
      setOrder((prev) => {
        if (!prev || prev.id !== assignOrder.id) return prev;
        if (assignMode === 'assign') {
          return patchOrderAfterAssignment(
            { ...prev, serviceDuration: billing.serviceDuration },
            operatorIds ?? [],
            undefined,
            staff,
          );
        }
        return patchOrderAfterOperation(prev, operatorIds, billing, staff);
      });
      setAssignOrder(null);
      showApiResultModal({ message: result.message, success: true });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to update order.',
        success: false,
      });
    } finally {
      setAssignSubmitting(false);
    }
  }, [assignMode, assignOrder, assignSubmitting]);

  const currentUserId = profile ? String(profile.userID) : null;
  const canViewPrivacy = canViewOrderTeamPrivacy(profile, order?.teamId, order?.teamName);
  const paymentAction = order && canViewPrivacy ? resolveInvoiceCloseAction(order) : null;

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <SubpageHeader
        title="Order Detail"
        subtitle={order ? `#${order.id}` : undefined}
        onBack={() => navigate(backTo)}
      />

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-16">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-green-500 text-2xl" />
            <p className="text-gray-400 text-xs mt-2">Loading order…</p>
          </div>
        </div>
      ) : error || !order ? (
        <div className="px-4 pt-10 text-center">
          <i className="ri-file-unknow-line text-3xl text-gray-300 mb-2 block" />
          <p className="text-sm text-gray-500 font-medium">Unable to load order</p>
          <p className="text-xs text-gray-400 mt-1">{error || 'Order not found.'}</p>
        </div>
      ) : (
        <OrderDetailView
          order={order}
          currentUserId={currentUserId}
          canViewPrivacy={canViewPrivacy}
          onServiceComplete={() => void handleCompleteService(order)}
          serviceCompleteSubmitting={completeSubmitting}
          onAssign={
            canTeamAdminEditWorkHours(order, canViewPrivacy) && !orderHasAssignedStaff(order)
              ? () => {
                  setAssignMode('assign');
                  setAssignOrder(order);
                }
              : undefined
          }
          onOrderOperation={
            canTeamAdminEditOrderBeforeComplete(order, canViewPrivacy)
              ? () => {
                  setAssignMode('billing');
                  setAssignOrder(order);
                }
              : undefined
          }
          onApplyInvoice={
            canViewPrivacy
              ? (next) => setInvoiceOrder(next)
              : undefined
          }
          onConfirmPayment={
            paymentAction
              ? (next) => setPaymentOrder(next)
              : undefined
          }
          confirmPaymentSubmitting={paymentSubmitting}
          confirmPaymentLabel={
            paymentAction ? invoiceCloseActionLabel(paymentAction) : undefined
          }
          onReorder={canViewPrivacy ? () => setReorderOpen(true) : undefined}
        />
      )}

      <InvoiceOrderModal
        order={invoiceOrder}
        onClose={() => setInvoiceOrder(null)}
        onInvoiceSent={markInvoiceSent}
        onConfirmPayment={(sent) => {
          markInvoiceSent(sent);
          setInvoiceOrder(null);
          setPaymentOrder({ ...sent, invoiceSent: true });
        }}
      />

      <ConfirmPaymentCloseModal
        order={paymentOrder}
        action={paymentOrder ? resolveInvoiceCloseAction(paymentOrder) : null}
        submitting={paymentSubmitting}
        onClose={() => {
          if (!paymentSubmitting) setPaymentOrder(null);
        }}
        onConfirm={(details) => {
          if (!paymentOrder) return;
          void handleConfirmPaymentClose(paymentOrder, details);
        }}
      />

      <AssignOrderModal
        order={assignOrder}
        mode={assignMode}
        submitting={assignSubmitting}
        onClose={() => {
          if (!assignSubmitting) setAssignOrder(null);
        }}
        onConfirm={(ids, billing, staff) => void handleConfirmAssign(ids, billing, staff)}
      />

      <ReorderOrderModal
        order={reorderOpen ? order : null}
        submitting={reorderSubmitting}
        onClose={() => {
          if (!reorderSubmitting) setReorderOpen(false);
        }}
        onConfirm={(payload) => void handleConfirmReorder(payload)}
      />
    </div>
  );
}
